import type Pusher from 'pusher-js'
import { IDLE_DISCONNECT_GRACE_MS, PUSHER_ACTIVE_STATES } from './attachIdleDisconnect.consts'

interface AttachIdleDisconnectParams {
  pusher: Pusher
}

/**
 * Wires a `visibilitychange` listener that disconnects the Pusher socket after the tab has been
 * continuously hidden for `IDLE_DISCONNECT_GRACE_MS`, and reconnects it when the tab is visible.
 *
 * Returns a `detach` function that removes the listener and clears any pending timer. Call it before
 * wiring a replacement pusher (e.g. when the Apollo client is recreated on a business switch) so a
 * listener bound to the discarded instance doesn't leak and pin its socket open.
 *
 * pusher-js rejoins its channels on reconnect, but the server has already dropped the GraphQL
 * subscriptions behind them; `pusherLink` counts the reconnect so they get re-registered.
 */
export const attachIdleDisconnect = ({ pusher }: AttachIdleDisconnectParams): (() => void) => {
  let disconnectTimer: ReturnType<typeof setTimeout> | undefined

  const clearDisconnectTimer = () => {
    clearTimeout(disconnectTimer)
    disconnectTimer = undefined
  }

  const armDisconnectTimer = () => {
    disconnectTimer = setTimeout(() => {
      if (PUSHER_ACTIVE_STATES.includes(pusher.connection.state)) {
        pusher.disconnect()
      }
    }, IDLE_DISCONNECT_GRACE_MS)
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      armDisconnectTimer()
      return
    }

    clearDisconnectTimer()

    if (!PUSHER_ACTIVE_STATES.includes(pusher.connection.state)) {
      pusher.connect()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Handle the tab being backgrounded at attach time (e.g. app opened in a background tab).
  if (document.hidden) {
    armDisconnectTimer()
  }

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    clearDisconnectTimer()
  }
}
